(() => {
  const HIDDEN_CLASS = 'shopify_subscriptions_app_block--hidden';
  const SETTLE_DEBOUNCE_MS = 80;
  const SETTLE_TIMEOUT_MS = 2000;

  function getVariants(productComponent) {
    const variantsJson = productComponent.querySelector('[data-variant-json]');
    if (!variantsJson?.textContent) return [];

    try {
      return JSON.parse(variantsJson.textContent);
    } catch {
      return [];
    }
  }

  function getSelectedVariant(productComponent) {
    const currentVariantJson = productComponent.querySelector('[data-current-variant-json]');
    if (currentVariantJson?.textContent?.trim()) {
      try {
        return JSON.parse(currentVariantJson.textContent);
      } catch {
        /* use live DOM lookup */
      }
    }

    const variants = getVariants(productComponent);
    if (!variants.length) return null;

    const variantInput = productComponent.querySelector(
      '[data-product-select], select[name="id"], input[name="id"]'
    );
    if (variantInput?.value) {
      return variants.find((variant) => String(variant.id) === String(variantInput.value)) || null;
    }

    const checkedOptions = productComponent.querySelectorAll(
      'input[type="radio"][data-index^="option"]:checked'
    );
    if (checkedOptions.length) {
      const selected = {};
      checkedOptions.forEach((input) => {
        selected[input.dataset.index] = input.value;
      });

      return (
        variants.find((variant) =>
          Object.entries(selected).every(([key, value]) => variant[key] === value)
        ) || null
      );
    }

    return null;
  }

  function updateCurrentVariantJson(productComponent, variant) {
    const currentVariantJson = productComponent.querySelector('[data-current-variant-json]');
    if (currentVariantJson && variant) {
      currentVariantJson.textContent = JSON.stringify(variant);
    }
  }

  function getWidgetContainer(productComponent) {
    return productComponent.querySelector('.shopify_subscriptions_app_container');
  }

  function getSubscriptionBlock(container, variantId) {
    if (!container) return null;

    if (variantId) {
      const scoped = container.querySelector(
        `section.shopify_subscriptions_app_block[data-variant-id="${variantId}"]:not(.${HIDDEN_CLASS})`
      );
      if (scoped) return scoped;
    }

    return container.querySelector(
      `section.shopify_subscriptions_app_block:not(.${HIDDEN_CLASS})`
    );
  }

  function getOneTimeRadio(block) {
    return block?.querySelector('input[type="radio"][data-radio-type="one_time_purchase"]');
  }

  function getSubscribeRadio(block) {
    return block?.querySelector('input[type="radio"][data-radio-type="selling_plan_group"]');
  }

  function clearSellingPlanInputs(productComponent) {
    productComponent.querySelectorAll('.selected-selling-plan-id').forEach((input) => {
      input.value = '';
    });
  }

  function isPreferenceApplied(block, preference, productComponent) {
    if (!block) return false;

    const oneTime = getOneTimeRadio(block);
    const subscribe = getSubscribeRadio(block);
    const hiddens = productComponent.querySelectorAll('.selected-selling-plan-id');
    const sellingPlanEmpty = [...hiddens].every((el) => el.value === '');

    if (preference === 'one_time') {
      return Boolean(oneTime?.checked && !subscribe?.checked && sellingPlanEmpty);
    }

    if (preference === 'subscription') {
      return Boolean(subscribe?.checked && !oneTime?.checked);
    }

    return false;
  }

  function forceOneTimePurchase(productComponent, variantId) {
    const block = getSubscriptionBlock(getWidgetContainer(productComponent), variantId);
    if (!block) return false;

    const oneTime = getOneTimeRadio(block);
    if (!oneTime) return false;

    if (isPreferenceApplied(block, 'one_time', productComponent)) return true;

    oneTime.checked = true;
    oneTime.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function forceSubscriptionPurchase(productComponent, variantId) {
    const block = getSubscriptionBlock(getWidgetContainer(productComponent), variantId);
    if (!block) return false;

    if (isPreferenceApplied(block, 'subscription', productComponent)) return true;

    const subscribe = getSubscribeRadio(block);
    if (!subscribe) return false;

    subscribe.checked = true;
    subscribe.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function applyPreference(productComponent, preference, variant) {
    if (!variant?.available) return false;

    if (preference === 'one_time') {
      return forceOneTimePurchase(productComponent, variant.id);
    }

    if (preference === 'subscription') {
      return forceSubscriptionPurchase(productComponent, variant.id);
    }

    return false;
  }

  function updateLoyaltyPoints(productComponent, radio) {
    const pdpLoyaltyPoints = productComponent.querySelector('[data-pdp-loyalty-points]');
    if (!pdpLoyaltyPoints || !radio?.checked || !radio.dataset.variantPrice) return;

    const loyaltyMultiplier = parseFloat(pdpLoyaltyPoints.dataset.loyaltyMultiplier);
    const priceNumber = parseFloat(radio.dataset.variantPrice.replace(/[^0-9.]/g, ''));
    const points = Math.floor(priceNumber);
    const pointsEl = pdpLoyaltyPoints.querySelector('.points');

    if (pointsEl) {
      pointsEl.innerHTML = points * loyaltyMultiplier;
    }
  }

  function initProductComponent(productComponent) {
    let lastVariant = getSelectedVariant(productComponent);
    let purchasePreference = 'one_time';
    let isProgrammaticChange = false;
    let activeVariantId = null;
    let settleObserver = null;
    let settleTimeout = null;
    let debounceTimeout = null;

    function setPurchaseOptionSyncing(isSyncing) {
      if (isSyncing) {
        productComponent.dataset.purchaseOptionSyncing = 'true';
      } else {
        delete productComponent.dataset.purchaseOptionSyncing;
      }
    }

    function stopSettleObserver() {
      settleObserver?.disconnect();
      settleObserver = null;
      clearTimeout(debounceTimeout);
      clearTimeout(settleTimeout);
      debounceTimeout = null;
      settleTimeout = null;
    }

    function finishPurchaseOptionSync() {
      stopSettleObserver();
      setPurchaseOptionSyncing(false);
    }

    function isBlockReadyForVariant(block, variantId) {
      if (!block) return false;
      if (!block.dataset.variantId) return true;
      return String(block.dataset.variantId) === String(variantId);
    }

    function bindWidgetListeners() {
      const container = getWidgetContainer(productComponent);
      if (!container || container.dataset.purchaseOptionBound === 'true') return;

      container.dataset.purchaseOptionBound = 'true';

      container.addEventListener('click', (event) => {
        const radio = event.target.closest('input[type="radio"][data-radio-type]');
        if (!radio) return;

        setPurchaseOptionSyncing(false);

        if (radio.dataset.radioType === 'one_time_purchase') {
          purchasePreference = 'one_time';
        } else if (radio.dataset.radioType === 'selling_plan_group') {
          purchasePreference = 'subscription';
        }
      });

      container.addEventListener('change', (event) => {
        const target = event.target;

        if (
          event.isTrusted &&
          target.matches('.group_dropdown, .shopify_subscriptions_group_dropdown')
        ) {
          purchasePreference = 'subscription';
          setPurchaseOptionSyncing(false);
          return;
        }

        if (target.matches('input[type="radio"]')) {
          updateLoyaltyPoints(productComponent, target);
        }
      });
    }

    function tryApplyPreference(variant) {
      if (!variant?.available) return false;

      const block = getSubscriptionBlock(getWidgetContainer(productComponent), variant.id);
      if (!isBlockReadyForVariant(block, variant.id)) return false;

      isProgrammaticChange = true;
      applyPreference(productComponent, purchasePreference, variant);
      isProgrammaticChange = false;

      const updatedBlock = getSubscriptionBlock(getWidgetContainer(productComponent), variant.id);
      const checkedRadio = updatedBlock?.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        updateLoyaltyPoints(productComponent, checkedRadio);
      }

      return (
        isBlockReadyForVariant(updatedBlock, variant.id) &&
        isPreferenceApplied(updatedBlock, purchasePreference, productComponent)
      );
    }

    function scheduleApplyPreference(variant) {
      if (!variant?.available) return;

      const variantId = variant.id;
      activeVariantId = variantId;
      stopSettleObserver();

      function attemptApply() {
        if (String(activeVariantId) !== String(variantId)) return;

        if (tryApplyPreference(variant)) {
          finishPurchaseOptionSync();
        }
      }

      attemptApply();

      const container = getWidgetContainer(productComponent);
      if (!container) return;

      settleObserver = new MutationObserver(() => {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(attemptApply, SETTLE_DEBOUNCE_MS);
      });

      settleObserver.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });

      settleTimeout = setTimeout(finishPurchaseOptionSync, SETTLE_TIMEOUT_MS);
    }

    function syncPurchaseUI(variant) {
      if (!variant) return;

      stopSettleObserver();
      productComponent.dataset.purchaseMode = variant.available ? 'available' : 'sold-out';

      if (variant.available) {
        setPurchaseOptionSyncing(true);
        scheduleApplyPreference(variant);
      } else {
        finishPurchaseOptionSync();
        clearSellingPlanInputs(productComponent);
      }
    }

    function runSync(variant) {
      bindWidgetListeners();

      if (variant) {
        lastVariant = variant;
        updateCurrentVariantJson(productComponent, variant);
      }

      const activeVariant = variant || lastVariant || getSelectedVariant(productComponent);
      syncPurchaseUI(activeVariant);
    }

    runSync(lastVariant);

    productComponent.addEventListener('variantChange', (event) => {
      runSync(event.detail?.variant);
    });
  }

  function init() {
    document
      .querySelectorAll('[data-subscription-purchase-options]')
      .forEach(initProductComponent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
